import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartCtx = createContext(null);
const STORAGE_KEY = "autostore_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); }
    catch { return []; }
  });
  const [open, setOpen] = useState(false);
  // Applied promo code (validated server-side later)
  const [coupon, setCouponState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("autostore_coupon") || "null"); }
    catch { return null; }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem("autostore_coupon", JSON.stringify(coupon));
  }, [coupon]);

  const value = useMemo(() => {
    const add = (product, opts = {}) => {
      // opts: { custom_price?: number }
      setItems((cur) => {
        const ex = cur.find((i) => i.product_id === product.id);
        if (ex) {
          return cur.map((i) =>
            i.product_id === product.id
              ? {
                  ...i,
                  quantity: i.quantity + 1,
                  // If a new custom price is supplied (and higher than existing), adopt it
                  custom_price:
                    opts.custom_price != null && opts.custom_price > (i.custom_price ?? i.price)
                      ? Number(opts.custom_price)
                      : (i.custom_price ?? null),
                }
              : i,
          );
        }
        return [
          ...cur,
          {
            product_id: product.id,
            title: product.title,
            price: product.price, // list price (never mutated)
            custom_price: opts.custom_price != null ? Number(opts.custom_price) : null, // pay-what-you-want
            image: product.image_url || (product.image_data ? `data:image/png;base64,${product.image_data}` : ""),
            quantity: 1,
          },
        ];
      });
      setOpen(true);
    };
    const remove = (pid) => setItems((cur) => cur.filter((i) => i.product_id !== pid));
    const setQty = (pid, q) =>
      setItems((cur) => cur.map((i) => (i.product_id === pid ? { ...i, quantity: Math.max(1, q) } : i)));
    const clear = () => { setItems([]); setCouponState(null); };
    const setCoupon = (c) => setCouponState(c);
    // Effective unit price = custom_price if set else list price
    const effUnit = (i) => (i.custom_price != null ? Number(i.custom_price) : i.price);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const subtotal = items.reduce((s, i) => s + effUnit(i) * i.quantity, 0);
    return { items, add, remove, setQty, clear, count, total: subtotal, subtotal, open, setOpen, coupon, setCoupon };
  }, [items, open, coupon]);

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
